import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useScreenWidth } from '@/hooks/useScreenWidth';
import { useToast } from '@/components/Toast';
import { useInviteLeaseDetailsDrawerStore } from '@/stores/use-invite-lease-details-drawer.store';
import { InviteLeaseDetailsDrawerFormContent } from './invite-lease-details.drawer.form-content';
import { theme } from '@theguarantors/tg-package-material-ui';
import { Renter } from '@/utils/types/renter.interface';
import { createInviteLeaseDetailsSuccessToastParams } from './components';
import { useInviteDrawerSessionTracking, useInviteDrawerQueryParamSync } from './hooks';
import type { CloseReason } from './types';

const getDrawerProps = (isDesktop: boolean) =>
  isDesktop
    ? {
        headerPadding: { py: 2, px: 4 },
        contentPadding: { py: 3, px: 4 },
        paperWidth: '520px',
        anchor: 'right' as const,
        paperHeight: '100%',
        borderRadius: '0',
      }
    : {
        headerPadding: { pt: '24px', pb: '16px', px: 2 },
        contentPadding: { py: '16px', px: 2 },
        paperWidth: '100%',
        anchor: 'bottom' as const,
        paperHeight: 'min-content',
        borderRadius: '24px 24px 0 0',
      };

export const InviteLeaseDetailsDrawer = () => {
  const { isDesktop } = useScreenWidth();
  const { isOpen, isFormDirty, closeDrawer, openCleanForm, formKey, setNewDealEmail } =
    useInviteLeaseDetailsDrawerStore();

  const queryClient = useQueryClient();
  const toast = useToast();
  const drawerProps = getDrawerProps(isDesktop);

  const { recordInviteFlowMilestone, trackDrawerClosed } = useInviteDrawerSessionTracking();
  useInviteDrawerQueryParamSync();

  // Ref so the unmount cleanup always calls the latest trackDrawerClosed without it being a dep.
  const trackDrawerClosedRef = useRef(trackDrawerClosed);
  const hasOpenSessionRef = useRef(false);

  useEffect(() => {
    trackDrawerClosedRef.current = trackDrawerClosed;
  }, [trackDrawerClosed]);

  useEffect(() => {
    hasOpenSessionRef.current = isOpen;
  }, [isOpen]);

  // Fires DrawerClosed on unmount only when the drawer was still open (e.g., route change).
  // closeWithReason() clears hasOpenSessionRef first, so normal closes don't double-fire here.
  useEffect(() => {
    return () => {
      if (hasOpenSessionRef.current) {
        trackDrawerClosedRef.current('unmount');
      }
    };
  }, []);

  const closeWithReason = (reason: CloseReason) => {
    hasOpenSessionRef.current = false;
    trackDrawerClosed(reason);
    closeDrawer();
  };

  const showSuccessToast = (emails: string[]) => {
    toast.success(
      createInviteLeaseDetailsSuccessToastParams({
        emails,
        onInviteAnother: () => openCleanForm('invite_another_renter_button'),
        theme,
      }),
    );
  };

  const handleSuccess = (values: Renter) => {
    const emails = values.applicantEmails?.map((e) => e.value).filter(Boolean) ?? [];
    const primaryEmail = values.applicantEmails?.[0]?.value?.trim();
    if (primaryEmail) {
      setNewDealEmail(primaryEmail);
    }
    recordInviteFlowMilestone('hadSubmitSuccess');
    closeWithReason('submit_success');
    showSuccessToast(emails);
    queryClient.invalidateQueries({ queryKey: ['applications'] });
    queryClient.invalidateQueries({ queryKey: ['applications-pagination'] });
    queryClient.invalidateQueries({ queryKey: ['deal-type-counts'] });
  };

  const backdrop = {
    id: 'renter-invite-drawer-backdrop',
    'data-testid': 'renter-invite-drawer-backdrop',
    style: {
      backgroundColor: 'rgba(8, 10, 45, 0.4)',
    },
  };

  return (
    <Drawer
      anchor={drawerProps.anchor}
      open={isOpen}
      onClose={(_event, reason) => {
        if (!isFormDirty()) {
          closeWithReason(reason === 'escapeKeyDown' ? 'escape' : 'click_outside');
        }
      }}
      slotProps={{ backdrop }}
      sx={{
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          border: 'none',
          borderRadius: drawerProps.borderRadius,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          height: drawerProps.paperHeight,
          width: drawerProps.paperWidth,
          overflow: 'hidden',
          [theme.breakpoints.down('md')]: {
            maxHeight: 'calc(100% - 105px)',
            height: '100%',
          },
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]}`,
          ...drawerProps.headerPadding,
        }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'background.default',
            flexShrink: 0,
          }}
          p={0}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontSize: '24px !important',
                lineHeight: '36px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '18px !important',
                  lineHeight: '24px !important',
                },
              }}
            >
              Invite your applicants
            </Typography>
            <IconButton
              color="inherit"
              aria-label="close drawer"
              onClick={() => closeWithReason('explicit_close')}
              sx={{ mr: '-12px' }}
              edge="start"
            >
              <CloseIcon
                sx={{
                  [theme.breakpoints.down('md')]: {
                    fontSize: '20px',
                  },
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Stack
        sx={{
          flex: 1,
          overflowY: 'auto',
          boxSizing: 'border-box',
          ...drawerProps.contentPadding,
          pb: 0,
          [theme.breakpoints.down('md')]: {
            overflowX: 'hidden',
          },
        }}
      >
        <InviteLeaseDetailsDrawerFormContent
          key={formKey}
          onSuccess={handleSuccess}
          onMilestoneReached={recordInviteFlowMilestone}
        />
      </Stack>
    </Drawer>
  );
};
